// ──────────────────────────────────────────────────────
// CardRenderer — dispatches a ProductSuggestion to the correct card
// component based on the NEXT_PUBLIC_PREMIUM_CARDS flag, product.role,
// and an optional layout hint from the SSE `meta` field.
//
// Flag semantics (read at build time via process.env):
//   QCARE-AI-DEV-UI-POLISH-V2 (2026-05-08): inverted from
//   "opt-in" (=== '1' enables) to "opt-out" (=== '0' disables) so
//   PROD/STG inherit the premium hero/budget layout by default
//   without requiring a build-time env update. DEV's .env.local
//   already has NEXT_PUBLIC_PREMIUM_CARDS=1 (still enabled — no
//   change). To rollback to legacy cards, set
//   NEXT_PUBLIC_PREMIUM_CARDS=0 explicitly at build time.
//
//   NEXT_PUBLIC_PREMIUM_CARDS === '0'  -> force ProductCardLegacy
//   anything else (unset, '1', etc.)   -> premium path active
//
// Layout override (independent of the flag):
//   layout === 'legacy_multi'          -> force ProductCardLegacy even when
//                                          a role is set (e.g. routines /
//                                          side-by-side comparisons that
//                                          look weird with one giant hero)
// ──────────────────────────────────────────────────────
import type { ProductSuggestion, ChatLayout } from '@/lib/types';
import { ProductCardLegacy } from './ProductCardLegacy';
import { HeroCard } from './HeroCard';
import { BudgetCard } from './BudgetCard';

const PREMIUM_ENABLED = process.env.NEXT_PUBLIC_PREMIUM_CARDS !== '0';

export function CardRenderer({
    product,
    layout,
}: {
    product: ProductSuggestion;
    layout?: ChatLayout;
}) {
    if (!PREMIUM_ENABLED) {
        return <ProductCardLegacy product={product} />;
    }
    if (layout === 'legacy_multi') {
        return <ProductCardLegacy product={product} />;
    }
    if (product.role === 'hero') {
        return <HeroCard product={product} />;
    }
    if (product.role === 'budget') {
        return <BudgetCard product={product} />;
    }
    return <ProductCardLegacy product={product} />;
}
